import { OeeQuote } from "@/features/ava-add-quotes/_logic/types";
import { SupabaseClient } from "@supabase/supabase-js";
import { enrichVenueWithChainAndBrand, fetchChainAndBrandMaps } from "../database/client/database";
import { EventVenueCandidate, StaticVenue } from "../database/venues";
import { CustomerDecisionStatus } from "../shared/enums";
import { sortQuotesForDisplay } from "./quotes";

/**
 * Drops archived quotes and puts the rest in display order. The `venue_options` view
 * aggregates a venue's quotes with no ordering of its own, and a venue can hold several
 * active quotes at once, so without this every consumer's `find(q => q.is_active)` would
 * depend on whatever order Postgres returned.
 *
 * @param quotes - Raw `oee_quotes` rows from the view, or null.
 * @returns Unarchived quotes, newest first.
 */
function toDisplayQuotes(quotes: OeeQuote[] | null | undefined): OeeQuote[] {
  return sortQuotesForDisplay((quotes || []).filter((q: OeeQuote) => !q.archived_at));
}

export interface VenueOption {
  candidate: EventVenueCandidate;
  venue: StaticVenue;
  quotes: OeeQuote[];
  email_threads: string[];
}

/**
 * Whether a venue candidate is an "active" proposal venue — one the team is
 * genuinely working: a proposal/RFP was sent (automated or manual) OR an active
 * in-app quote exists, AND the customer has not declined it and it has not been
 * removed or archived. Single source of truth shared by the proposals-grid "All"
 * count and the contract-upload venue picker so both surfaces agree on which
 * venues "count". `quotes` here are already `archived_at`-filtered by
 * {@link getVenueOptions}/{@link getVenueOption}.
 *
 * @param option - The venue option bundle (candidate + quotes) for one candidate.
 * @returns `true` when the venue is active per the rule above.
 */
export function isActiveProposalVenue(option: VenueOption): boolean {
  const candidate = option.candidate;
  if (!candidate) return false;

  const proposalSent = candidate.timestamp_rfp_sent != null || candidate.timestamp_manual_rfp_submitted != null;
  const hasActiveQuote = option.quotes?.some((quote) => quote.is_active === true) ?? false;
  if (!proposalSent && !hasActiveQuote) return false;

  const notDeclined = candidate.customer_decision_status !== CustomerDecisionStatus.DECLINED;
  return notDeclined && !candidate.customer_removed && !candidate.archived;
}

/**
 * Drops venue options whose candidate an admin has archived. Setting
 * `event_venue_candidate.archived = true` is how the admin dashboard removes a
 * venue from the customer/enterprise view, so every enterprise-facing surface
 * (proposals grid, compare, home funnel) must exclude it. Admin-facing callers
 * read {@link getVenueOptions} directly and intentionally keep archived rows so
 * they can be reviewed and unarchived.
 *
 * @param options - Venue options to filter.
 * @returns Only the options whose candidate is not archived.
 */
export function excludeArchivedCandidates(options: VenueOption[]): VenueOption[] {
  return options.filter((option) => !option.candidate?.archived);
}

/**
 * Enterprise-facing variant of {@link getVenueOptions}: returns the event's venue options
 * with admin-archived candidates already removed. This is the single definition of
 * "what an enterprise/customer surface should see" for the `VenueOption[]` path — every
 * enterprise array-consumer (proposals grid, compare, home funnel) routes through here so
 * the archived-exclusion rule lives in one place. Admin surfaces call the raw
 * {@link getVenueOptions} to retain archived rows.
 *
 * @param supabase - The Supabase client.
 * @param eventId - The ID of the event.
 * @returns The non-archived venue options.
 * @throws An error if there is an issue fetching the venue options.
 */
export async function getEnterpriseVenueOptions(supabase: SupabaseClient, eventId: number): Promise<VenueOption[]> {
  const options = await getVenueOptions(supabase, eventId);
  return excludeArchivedCandidates(options);
}

/**
 * Drops rows the caller cannot resolve a venue for, so `VenueOption.venue` is
 * actually non-null as its type claims.
 *
 * `nowadays.venue_options` is a `security_invoker` view that LEFT JOINs the
 * RLS-gated `venue_static`. `event_venue_candidate.venue_static_id` is NOT NULL
 * with an FK, so the join can never miss for data reasons — a null `venue` means
 * one thing only: RLS hid that venue from this caller. The view reports that by
 * nulling the whole composite column rather than dropping the row, so consumers
 * that reasonably trust the type dereference null and take their page down.
 *
 * Filtering here rather than at each call site keeps the invariant in one place:
 * every `VenueOption` consumer reaches it through this module. It is a no-op for
 * service-role callers, which bypass RLS and never see a null venue.
 *
 * Note this does not cover every reader of the underlying view: `database.ts` and
 * `database_admin.ts` query `nowadays.venue_options` directly, and `database.ts`
 * still carries the `|| values.venue!` assertion this replaced. They are
 * admin/service-role surfaces, so the null cannot arise there today.
 *
 * @param rows - Raw view rows paired with the venue resolved for them.
 * @param eventId - Event being loaded, for the diagnostic.
 * @returns Only the rows with a readable venue.
 */
function dropUnreadableVenues<T extends { venue: StaticVenue | null }>(
  rows: T[],
  eventId: number
): (T & { venue: StaticVenue })[] {
  const readable = rows.filter((row): row is T & { venue: StaticVenue } => row.venue != null);

  if (readable.length !== rows.length) {
    // Not silent: a caller missing venues they should see means the RLS predicate
    // and the app's access check disagree, which is worth finding in the logs.
    console.warn(
      `getVenueOptions: hid ${rows.length - readable.length}/${rows.length} venue(s) for event ${eventId} — not readable under this caller's RLS context`
    );
  }

  return readable;
}

/**
 * Gets venue options for an event.
 *
 * Venues the caller cannot read under RLS are dropped rather than returned with a
 * null `venue` — see {@link dropUnreadableVenues}.
 *
 * @param supabase - The Supabase client
 * @param eventId - The ID of the event
 * @returns The venue options
 * @throws An error if there is an issue fetching the venue options
 */
export async function getVenueOptions(supabase: SupabaseClient, eventId: number): Promise<VenueOption[]> {
  const { data, error } = await supabase.schema("nowadays").from("venue_options").select("*").eq("event_id", eventId);

  if (error) {
    throw new Error(`Error fetching candidates with venue and quotes for event ${eventId}: ${error.message}`);
  }

  const { chainMap, brandMap } = await fetchChainAndBrandMaps(supabase, data);

  const options = data.map((values) => {
    return {
      candidate: values.candidate,
      venue: enrichVenueWithChainAndBrand(values.venue, chainMap, brandMap),
      quotes: toDisplayQuotes(values.oee_quotes),
      email_threads: values.email_threads || [],
    };
  });

  return dropUnreadableVenues(options, eventId);
}

/**
 * Gets a single venue option for an event + venue. Filters at the database on
 * the view's top-level `venue_static_id` column so only one row is returned,
 * instead of loading the event's full venue_options bundle and finding one
 * client-side.
 *
 * @param supabase - The Supabase client
 * @param eventId - The ID of the event
 * @param venueStaticId - The venue static ID to load
 * @returns The venue option, or null if no matching row exists
 * @throws An error if there is an issue fetching the venue option
 */
export async function getVenueOption(
  supabase: SupabaseClient,
  eventId: number,
  venueStaticId: number
): Promise<VenueOption | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("venue_options")
    .select("*")
    .eq("event_id", eventId)
    .eq("venue_static_id", venueStaticId)
    .maybeSingle();

  if (error) {
    console.error("getVenueOption:", { eventId, venueStaticId }, error);
    throw new Error("Failed to fetch venue option");
  }

  if (!data) return null;

  const { chainMap, brandMap } = await fetchChainAndBrandMaps(supabase, [data]);

  const venue = enrichVenueWithChainAndBrand(data.venue, chainMap, brandMap);
  // A venue this caller cannot read under RLS is indistinguishable from one that
  // does not exist, so report it the same way rather than hand back a null venue.
  if (!venue) {
    console.warn(
      `getVenueOption: venue ${venueStaticId} on event ${eventId} is not readable under this caller's RLS context`
    );
    return null;
  }

  return {
    candidate: data.candidate,
    venue,
    quotes: toDisplayQuotes(data.oee_quotes),
    email_threads: data.email_threads || [],
  };
}
